import { Component, ElementRef, OnDestroy } from "@angular/core";
import { AccessControlService } from "app/access-control/access-control-service";
import { LOCATION_TEAMS } from "app/access-control/common-permission-asks";
import { Teams } from "app/access-control/permissions";
import { PermissionsResponse } from "app/access-control/permissions-response";
import {
  AlertController,
  LoadingController,
  ModalController,
  PopoverController,
  ToastController,
} from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import * as moment from "moment";
import { CompanyTeamsService } from "providers/company-teams-service/company-teams-service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { Subscription } from "rxjs";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UtilityService } from "../../../providers/utility-service/utility-service";

@Component({
  selector: "location-teams",
  templateUrl: "location-teams.html",
})
export class LocationTeams implements OnDestroy {
  companyTeams: any[] = [];
  searchInput: string = "";
  searchBy: string = "team";
  searchMode: boolean = false;
  currentPage: number = 0;
  pages: number[] = [];
  totalPages: number = 0;
  totalElements: number = 0;
  companyTeamRolesTemplate: any[] = [];
  palliativeCareTeamToggle: boolean = false;
  sortedBy = "createdDatetime,desc";
  subscriptions: Subscription[] = [];
  sortOptions: SortOptions = new SortOptions("name", "asc");
  pageSize: number = 10;
  teamsPermissionsAccess = {} as PermissionsResponse;
  hasTeamsAll: boolean = false;
  hasTeamsAdd: boolean = false;
  hasTeamsEdit: boolean = false;
  hasTeamsDelete: boolean = false;

  constructor(
    private popover: PopoverController,
    private stateData: StateManagementService,
    private companyTeamsService: CompanyTeamsService,
    private loadingCtrl: LoadingController,
    private volunteerService: ResourceManagementService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private elementRef: ElementRef,
    private accessControlService: AccessControlService,
    private toastCtrl: ToastController,
    private utilityService: UtilityService
  ) {
    this.stateData.showPalliativeCareTeam.subscribe((active) => {
      if (active) {
        this.palliativeCareTeamToggle = active;
      }
    });
    this.checkLocationTeamsAccess();
  }

  checkLocationTeamsAccess() {
    this.subscriptions.push(
      this.accessControlService
        .getPermission(LOCATION_TEAMS)
        .subscribe((data) => {
          if (data) {
            this.teamsPermissionsAccess = data;
            this.getTeamsAccess();
          }
        })
    );
  }

  getTeamsAccess() {
    this.hasTeamsAll = this.accessControlService.hasAccess(
      this.teamsPermissionsAccess,
      Teams.TeamsAll
    );
    this.hasTeamsAdd = this.accessControlService.hasAccess(
      this.teamsPermissionsAccess,
      Teams.TeamsAdd
    );
    this.hasTeamsEdit = this.accessControlService.hasAccess(
      this.teamsPermissionsAccess,
      Teams.TeamsEdit
    );
    this.hasTeamsDelete = this.accessControlService.hasAccess(
      this.teamsPermissionsAccess,
      Teams.TeamsDelete
    );
  }

  ngOnInit() {
    this.getCompanyTeamRoles();
  }

  ngAfterViewInit() {
    // attaching event on search icon explictly
    const searchIcon = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    // ion-searchbar renders its icon asynchronously; without this guard a slow
    // render throws out of the lifecycle hook and takes the whole grid with it.
    if (searchIcon) {
      searchIcon.addEventListener("click", this.searchTeam.bind(this));
    }
  }

  getCompanyTeamRoles() {
    this.subscriptions.push(
      this.companyTeamsService.getCompanyTeamRoles().subscribe((data) => {
        const unOrderedRoles: any = data;
        const orderedRoles = unOrderedRoles.sort((a, b) => {
          if (a.abbreviation === "MD") return -1;
          else if (a.companyTeamRequired === b.companyTeamRequired)
            return a.name.localeCompare(b.name);
          else if (a.companyTeamRequired) return -1;
          else return 1;
        });
        this.companyTeamRolesTemplate = orderedRoles.map((role) => {
          role.members = [
            {
              displayName: "",
              email: "",
              firstName: "",
              id: null,
              lastName: "",
              personId: null,
              phoneNumber: null,
              qbosId: null,
            },
          ];
          return role;
        });
        this.getCompanyTeams();
        this.currentPage = 0;
      })
    );
  }

  getCompanyTeams() {
    let message = this.searchMode ? "Searching teams" : "Loading teams";
    this.utilityService.increaseSpinnerCount(message);
    this.subscriptions.push(
      this.companyTeamsService
        .getCompanyTeamsByToggle(
          this.currentPage,
          this.searchBy,
          this.searchInput,
          this.sortedBy,
          this.pageSize,
          this.palliativeCareTeamToggle
        )
        .subscribe(
          (data) => {
            const responseCompanyTeams: any = data || {};
            this.totalPages = responseCompanyTeams.totalPages || 0;
            // Guard the body: a throw in here skips the decrement below, leaving
            // the blocking spinner up and the grid stranded mid-load.
            const content = responseCompanyTeams.content || [];
            this.companyTeams = content.map((team) =>
              this.processCompanyTeam(team)
            );
            // Exactly one decrement per increment. Decrementing per ROW drove the
            // counter negative, after which later loads showed no spinner at all.
            this.utilityService.decreaseSpinnerCount();
          },
          (error) => {
            this.utilityService.decreaseSpinnerCount();
          }
        )
    );
  }

  processCompanyTeam(team) {
    const teamMembers = team.members;
    const leadIndex = teamMembers
      .map((member) => {
        if (member.role) {
          return member.role.name;
        } else {
          return "";
        }
      })
      .indexOf("Team Lead");
    const managerIndex = teamMembers
      .map((member) => {
        if (member.role) {
          return member.role.name;
        } else {
          return "";
        }
      })
      .indexOf("Case Manager");
    if (teamMembers[leadIndex] && teamMembers[leadIndex].displayName) {
      team.teamLead =
        teamMembers[leadIndex].lastName +
        ", " +
        teamMembers[leadIndex].firstName;
    } else {
      team.teamLead = "N/A";
    }
    if (teamMembers[managerIndex] && teamMembers[managerIndex].displayName) {
      team.caseManager =
        teamMembers[managerIndex].lastName +
        ", " +
        teamMembers[managerIndex].firstName;
    } else {
      team.caseManager = "N/A";
    }

    team.displayDetails = false;
    const teamRoles = JSON.parse(JSON.stringify(this.companyTeamRolesTemplate));
    team.members.forEach((member) => {
      if (member.role && member.role.id) {
        const index = this.companyTeamRolesTemplate
          .map((role) => role.id)
          .indexOf(member.role.id);
        member.displayName = member.lastName + ", " + member.firstName;
        // The role template and the teams page are fetched separately, so a
        // member can carry a role the template does not know (retired or added
        // in between) — indexOf is then -1. The throw would happen inside the
        // rows map, blanking EVERY row on the page, not just this one.
        if (index === -1 || !teamRoles[index]) {
          return;
        }
        if (teamRoles[index].members[0].id === null) {
          teamRoles[index].members = [member];
        } else {
          teamRoles[index].members.push(member);
        }

        if (member.contactId) {
          member["volunteer"] = this.volunteerService.getCredentialedResource(
            member.contactId
          );
        }
      }
    });
    team.roles = teamRoles;
    team.patients = [];
    return team;
  }

  clearSearch() {
    this.searchInput = "";
    if (this.searchMode) {
      this.currentPage = 0;
      this.searchMode = false;
      this.getCompanyTeams();
    }
  }

  checkClearInput() {
    if (!this.searchInput || this.searchInput.trim().length === 0) {
      this.clearSearch();
    }
  }

  searchTeam() {
    this.searchMode = true;
    this.currentPage = 0;
    if (this.searchInput) {
      this.getCompanyTeams();
    }
  }

  sortBy(selectedCol) {
    if (this.sortOptions.column === selectedCol) {
      this.sortOptions.direction =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }
    this.sortedBy = this.sortOptions.column + "," + this.sortOptions.direction;
    this.getCompanyTeams();
  }

  getTeamPatients(team) {
    if (!team.loadingPatients) {
      this.utilityService.increaseSpinnerCount(`Loading ${team.name} patients`);
    }
    this.subscriptions.push(
      this.companyTeamsService
        .getCompanyTeamPatients(team.id, team.currentPage, team.sortedBy)
        .subscribe(
          (data) => {
            const responsePatients: any = data || {};
            team.totalPages = responsePatients.totalPages;
            team.pages = Array.apply(null, { length: team.totalPages }).map(
              Number.call,
              Number
            );
            team.patients = responsePatients.content;
            this.utilityService.decreaseSpinnerCount();
            team.loadingPatients = false;
          },
          (error) => {
            this.utilityService.decreaseSpinnerCount();
            team.loadingPatients = false;
            console.log(error);
          }
        )
    );
  }

  getTeamPatientsByPage(team, page: number) {
    team.currentPage = page;
    this.getTeamPatients(team);
  }

  getTeamPatientsPreviousPage(team) {
    if (team.currentPage > 0) {
      team.currentPage = team.currentPage - 1;
      this.getTeamPatients(team);
    }
  }

  getTeamsPatientsNextPage(team) {
    if (team.currentPage < team.totalPages - 1) {
      team.currentPage = team.currentPage + 1;
      this.getTeamPatients(team);
    }
  }

  getCompanyTeamsByPage(page: number) {
    if (this.currentPage != page) {
      this.currentPage = page;
      this.getCompanyTeams();
    }
  }

  formatPhoneNumber(phone: string) {
    if (!phone) {
      return "";
    }
    let result = phone.replace(/[^0-9,eE\.\+]+/g, "");
    if (result.length > 6) {
      result =
        result.slice(0, 3) + "-" + result.slice(3, 6) + "-" + result.slice(6);
    } else if (result.length > 3) {
      result = result.slice(0, 3) + "-" + result.slice(3);
    }
    return result;
  }

  formatTimezoneDate(stringDate) {
    if (!stringDate) {
      return "";
    }
    return moment.utc(stringDate).local().format("MM/DD/YYYY");
  }

  formatDate(stringDate) {
    if (!stringDate) {
      return "";
    }
    return moment.utc(stringDate).format("MM/DD/YYYY");
  }

  toogleDisplayDetails(companyTeam) {
    companyTeam.displayDetails = !companyTeam.displayDetails;
    if (!companyTeam.patients.length) {
      companyTeam.currentPage = 0;
      companyTeam.loadingPatients = true;
      companyTeam.sortedBy = "lastName,asc";
      this.getTeamPatients(companyTeam);
    }
  }

  companyTeamOptions(ev, companyTeam) {
    const options = this.popover.create(
      "CompanyTeamsOptionsPage",
      {
        hasTeamsEdit: this.hasTeamsAll || this.hasTeamsEdit,
        hasTeamsDelete: this.hasTeamsAll || this.hasTeamsDelete,
      },
      { cssClass: "company-teams-options-popover" }
    );
    options.onDidDismiss((data) => {
      if (data) {
        if (data === "delete") {
          this.deleteCompanyTeam(companyTeam);
        } else if (data === "edit") {
          this.editCompanyTeam(companyTeam);
        }
      }
    });
    options.present({ ev });
  }

  newCompanyTeam() {
    const companyTeam: any = {
      id: null,
      name: "",
    };
    companyTeam.roles = this.companyTeamRolesTemplate.slice();
    let modal = this.modalCtrl.create(
      "CompanyTeamsModalPage",
      {
        mode: "new",
        companyTeam,
        palliativeCareTeamToggle: this.palliativeCareTeamToggle,
      },
      { cssClass: "company-teams-modal" }
    );
    modal.onDidDismiss((data) => {
      if (data) {
        const response: any = data;
        if (response.update) {
          this.getCompanyTeams();
        }
      }
    });
    modal.present();
  }

  editCompanyTeam(companyTeam) {
    let modal = this.modalCtrl.create(
      "CompanyTeamsModalPage",
      {
        mode: "edit",
        companyTeam,
        palliativeCareTeamToggle: this.palliativeCareTeamToggle,
      },
      { cssClass: "company-teams-modal" }
    );
    modal.onDidDismiss((data) => {
      if (data) {
        const response: any = data;
        if (response.update) {
          this.companyTeamsService
            .getCompanyTeamsByToggle(
              0,
              "team",
              response.companyTeam.name,
              this.sortedBy,
              7,
              this.palliativeCareTeamToggle
            )
            .subscribe((data) => {
              const teamResponse: any = data;
              if (teamResponse.content.length) {
                const index = this.companyTeams
                  .map((team) => {
                    return team.id;
                  })
                  .indexOf(companyTeam.id);
                if (index > -1) {
                  const responseCompanyTeam = teamResponse.content.filter(
                    (team) => team.id === companyTeam.id
                  )[0];
                  this.companyTeams[index] =
                    this.processCompanyTeam(responseCompanyTeam);
                  this.companyTeams[index]["displayDetails"] = true;
                }
              }
            });
        }
      }
    });
    modal.present();
  }

  deleteCompanyTeam(companyTeam) {
    const alert = this.alertCtrl.create({
      title: "Delete Team",
      message: `Are you sure you want to delete it?`,
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            this.companyTeamsService
              .deleteCompanyTeam(companyTeam.id)
              .subscribe(
                (data) => {
                  this.getCompanyTeams();
                },
                (error) => {
                  this.displayErrorResponse(error);
                }
              );
          },
        },
      ],
    });
    alert.present();
  }

  displayErrorResponse(error) {
    // A gateway 502 / network drop has no structured body. Reaching straight
    // into error.error.displayObject.error threw inside the error handler, so
    // the user got no feedback at all that the delete had failed.
    const message =
      (error &&
        error.error &&
        error.error.displayObject &&
        error.error.displayObject.error) ||
      "Something went wrong. Please try again.";
    let toast = this.toastCtrl.create({
      message,
      duration: 4000,
      position: "below",
      cssClass: "error",
    });
    toast.present();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((val) => val.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/location-teams/location-teams.ts